import React, { useEffect, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FiSearch } from "react-icons/fi";
import scannerApi from "../apis/scanner.api";
import { useNavigate } from "react-router-dom";

const BRAND = "#1d275f";

export default function ScannerEventListPage() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(12);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [type, setType] = useState("all");

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const monthIndex = date.getMonth();
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = months[monthIndex];
    const currentYear = new Date().getFullYear();

    if (date.getFullYear() > currentYear) {
      return `${day} ${month} ${date.getFullYear()}`;
    }
    return `${day} ${month}`;
  };

  const convertTo12HourFormat = (time24) => {
    if (!time24) return "TBD";
    const [hours, minutes] = time24.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;
    return `${String(hours12).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )} ${period}`;
  };

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const [debounceTimer, setDebounceTimer] = useState(null);

  const buildParams = () => {
    return {
      page,
      limit: perPage,
      title: search || undefined,
      city: city || undefined,
      state: state || undefined,
      type: type === "all" ? undefined : type,
    };
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const params = buildParams();
      scannerApi.eventList({
        params,
        success: (res) => {
          const payload = res?.data || res;
          const data = payload?.data || [];
          const meta = payload?.meta || {};
          setItems(data);
          setTotal(meta.total ?? data.length);
          setTotalPages(
            meta.totalPages ??
              Math.max(1, Math.ceil((meta.total ?? data.length) / perPage))
          );
        },
        error: (err) => {
          console.error("scannerApi.eventList error", err);
          showToast(
            "error",
            err?.response?.data?.message || "Failed to load content."
          );
        },
      });
    } catch (err) {
      console.error("fetchList error", err);
      showToast("error", "Unexpected error while fetching content.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const id = setTimeout(() => {
      setPage(1);
      fetchList();
    }, 450);
    setDebounceTimer(id);
    return () => clearTimeout(id);
  }, [search, city, state, type]);

  useEffect(() => {
    fetchList();
  }, [page]);

  const showToast = (type_, message, timeout = 2500) => {
    setToast({ type: type_, message });
    if (timeout) setTimeout(() => setToast(null), timeout);
  };

  const openScannerFor = (item) => {
    const qp = new URLSearchParams({
      content_id: item.content_id,
      type: item.type,
    }).toString();
    navigate(`/scanner/scan?${qp}`, { state: { content: item } });
  };

  const humanType = (t) => (t === "movie" ? "Movie" : "Event");

  const DesktopSkeleton = () => (
    <div className="grid grid-cols-3 gap-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg overflow-hidden shadow">
          <div className="h-40 bg-gray-100 animate-pulse" />
          <div className="p-4">
            <div className="h-4 bg-gray-100 rounded w-3/4 animate-pulse mb-2" />
            <div className="h-3 bg-gray-100 rounded w-1/2 animate-pulse mb-3" />
            <div className="h-8 bg-gray-100 rounded w-full animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );

  const MobileSkeleton = () => (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="bg-white rounded shadow p-4 flex gap-3">
          <div className="w-28 h-20 bg-gray-100 animate-pulse rounded" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-100 animate-pulse w-2/3 rounded" />
            <div className="h-3 bg-gray-100 animate-pulse w-1/2 rounded" />
            <div className="h-8 bg-gray-100 animate-pulse w-32 rounded mt-2" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: BRAND }}>
            Accessible Content
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Curated list of events and movies you can scan. Use filters and sort
            to narrow results.
          </p>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <div className="flex items-center bg-white border rounded shadow-sm px-3 py-2 w-96">
            <FiSearch className="text-gray-400 mr-2" />
            <input
              className="w-full outline-none text-sm"
              placeholder="Search title"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search by title"
            />
          </div>

          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="border rounded px-3 py-2 text-sm"
            aria-label="Filter by type"
          >
            <option value="all">All</option>
            <option value="event">Events</option>
            <option value="movie">Movies</option>
          </select>

          {/* Sort control */}

          <button
            onClick={() => setFilterOpen(true)}
            className="px-4 py-2 bg-white border rounded text-sm hover:shadow"
            title="Advanced filters"
          >
            Filters
          </button>
        </div>

        {/* mobile quick controls */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={() => setFilterOpen(true)}
            className="px-3 py-2 bg-white border rounded"
            aria-label="Open filters"
          >
            Filters
          </button>
        </div>
      </div>

      {/* Desktop grid */}
      <div className="hidden md:block mt-5">
        {loading ? (
          <DesktopSkeleton />
        ) : items.length === 0 ? (
          <div className="col-span-3 p-8 text-center text-gray-500">
            No accessible content found. Adjust filters or contact your
            organizer.
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-6">
            {items.map((it) => (
              <article
                key={it.content_id}
                className="bg-white rounded-lg shadow hover:shadow-md transition overflow-hidden"
              >
                <div className="h-40 w-full bg-gray-200 overflow-hidden">
                  <img
                    src={it.banner_image}
                    alt={it.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <h2 className="text-sm font-semibold text-gray-900">
                      {it.title}
                    </h2>
                    <span className="text-xs text-gray-500">
                      {humanType(it.type)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                    {it.address}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-xs text-gray-600">
                      <div>{it.city}</div>
                      <div className="mt-1">
                        {formatDate(it.start_date)} •{" "}
                        {convertTo12HourFormat(it.start_time)}
                      </div>
                    </div>
                    <div>
                      <button
                        onClick={() => openScannerFor(it)}
                        className="px-3 py-1 rounded text-white text-sm"
                        style={{ backgroundColor: BRAND }}
                      >
                        Open Scan
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-600">Total: {total}</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              className="px-3 py-1 border rounded"
              disabled={page <= 1}
            >
              Prev
            </button>
            <div className="px-3 py-1 border rounded text-sm">{page}</div>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              className="px-3 py-1 border rounded"
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Mobile list */}
      <div className="md:hidden">
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 flex items-center bg-white border rounded px-3 py-2">
              <FiSearch className="text-gray-400 mr-2" />
              <input
                className="w-full outline-none text-sm"
                placeholder="Search title"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search by title"
              />
            </div>

            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="border rounded px-3 py-2 text-sm"
              aria-label="Type"
            >
              <option value="all">All</option>
              <option value="event">Events</option>
              <option value="movie">Movies</option>
            </select>
          </div>

          {loading ? (
            <MobileSkeleton />
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No accessible content
            </div>
          ) : (
            items.map((it) => (
              <div
                key={it.content_id}
                className="bg-white rounded shadow p-4 flex gap-3"
              >
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">{it.title}</h3>
                    <span className="text-xs text-gray-500">
                      {humanType(it.type)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                    {it.address}
                  </p>
                  <div className="flex items-center justify-between mt-3">
                    <div className="text-xs text-gray-600">
                      {formatDate(it.start_date)} •{" "}
                      {convertTo12HourFormat(it.start_time)}
                    </div>
                    <button
                      onClick={() => openScannerFor(it)}
                      className="px-3 py-1 rounded text-white text-sm"
                      style={{ backgroundColor: BRAND }}
                    >
                      Scan
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            className="flex-1 px-3 py-2 border rounded"
            disabled={page <= 1}
          >
            Prev
          </button>
          <div className="px-3 py-2 border rounded text-sm text-center w-20">
            {page}
          </div>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            className="flex-1 px-3 py-2 border rounded"
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* Filters Dialog (Headless UI) */}
      <Transition show={filterOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40"
          onClose={() => setFilterOpen(false)}
        >
          <Transition.Child
            as="div"
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as="div"
              enter="ease-out duration-200 transform"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150 transform"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md bg-white rounded shadow p-6">
                <Dialog.Title className="text-lg font-semibold">
                  Filters
                </Dialog.Title>
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="text-xs text-gray-600">City</label>
                    <input
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="mt-1 w-full border rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">State</label>
                    <input
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="mt-1 w-full border rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">
                      Title contains
                    </label>
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="mt-1 w-full border rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setCity("");
                      setState("");
                      setSearch("");
                    }}
                    className="px-3 py-2 border rounded"
                  >
                    Reset
                  </button>
                  <button
                    onClick={() => {
                      setFilterOpen(false);
                      setPage(1);
                      fetchList();
                    }}
                    className="px-4 py-2 rounded text-white"
                    style={{ backgroundColor: BRAND }}
                  >
                    Apply
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Toast */}
      <div
        aria-live="assertive"
        className="fixed inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6"
      >
        <div className="w-full flex flex-col items-center space-y-4 sm:items-end">
          <Transition
            show={!!toast}
            as={Fragment}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            {toast && (
              <div
                className={`max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden ${
                  toast.type === "error"
                    ? "border-l-4 border-red-500"
                    : "border-l-4 border-blue-500"
                }`}
              >
                <div className="p-3">
                  <p className="text-sm text-gray-900">{toast.message}</p>
                </div>
              </div>
            )}
          </Transition>
        </div>
      </div>
    </div>
  );
}
